import { Flex, type FlexProps, Select, Text } from "@mantine/core";
import dayjs from "dayjs";
import { memo, useCallback, useMemo, useRef, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useIsExpired } from "src/generator/hooks/useIsExpired";
import { useIsNewEntry } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setInitiallyOpen } from "src/redux/slices/generatorSlice";

import { setReleaseEntryLock } from "../../../redux/slices/entrySlice";
import { EntrySelectItem } from "../EntrySelectItem/EntrySelectItem";
import { MEDLBID } from "../../../utils";
import { Badge } from "@mantine/core";
import { useGetApi } from "src/hooks";
import { mapSummary } from "../../../pages/Summary/Summary.utils";

const LogBarSelects = (props: FlexProps) => {
  const entriesRef = useRef<HTMLInputElement>(null);
  const isNewEntry = useIsNewEntry();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logbookId, idType, lbeidOrPartId } = useParams();
  const isExpiredLogbook = useIsExpired();
  const dispatch = useDispatch();
  const { entries, logbooks, latestEntry, collectionId } = useSelector(
    state => state.generatorSlice,
  );
  const allPendingNewEntries = useSelector(
    state => state.pendingRequestsSlice.pendingNewEntries,
  );

  const [reviewData, setReviewData] = useState<Array<any>>();
  const sophiaLbId = "aebb3679-9132-11ef-8b69-02c1fe2db5a5";
  const showVerifyCount = logbookId === sophiaLbId;

  const apiArgs = useMemo(
    () => ({
      request: "get-review",
      body: {
        params: { req: "all" },
        _logbook: logbookId,
        _status: "*",
      },
      mapper: mapSummary,
      fetchOnInit: false,
    }),
    [logbookId],
  );

  const { data: verifyData, refetch: fetchVerify } = useGetApi(apiArgs);

  useEffect(() => {
    if (!showVerifyCount) return;
    fetchVerify();
  }, [showVerifyCount]);

  useEffect(() => {
    if (verifyData?.reviews) {
      setReviewData(verifyData.reviews);
    } else {
      setReviewData(undefined);
    }
  }, [verifyData]);

  const pendingEntries = useMemo(
    () =>
      allPendingNewEntries
        .filter(p => p.lbid === logbookId)
        .map(p => ({
          group: "Offline",
          value: p.id,
          label: dayjs(p.created).format("D MMM YYYY - HH:mm"),
          status: "new",
        })),
    [allPendingNewEntries, logbookId],
  );
  const entriesSelect = useMemo(
    () =>
      pendingEntries.length > 0
        ? [
            ...pendingEntries,
            ...entries.map((e: any) => ({ ...e, group: "Online" })),
          ]
        : entries,
    [entries, pendingEntries],
  );

  const handleSelectChange = useCallback(
    (value: string) => {
      dispatch(setInitiallyOpen(false));
      dispatch(setReleaseEntryLock(Math.random()));

      if (entriesRef.current) entriesRef.current.blur();

      navigate(`/logbook/${logbookId}/${idType}/${value}`, { replace: true });
      if (isNewEntry) searchParams.delete("new");
    },
    [dispatch, isNewEntry, logbookId, navigate, idType, searchParams],
  );

  const handleLogbookChange = useCallback(
    (id: string | null) => {
      dispatch(setReleaseEntryLock(Math.random()));

      const lb = logbooks.find(logbook => logbook.value === id);
      if (lb?.lbtype === "collection") {
        navigate(`/collection/${lb.value}`);
      } else if (lb?.lbtype === "part") {
        navigate(`/logbook/${lb.value}/partid/?lbTypeStatus=null`);
      } else if (lb?.lbtype === "logbook") {
        if (lb.value === MEDLBID) {
          navigate(`/logbook/${lb.value}/meid`);
        } else {
          navigate(`/logbook/${lb.value}/lbeid`);
        }
      }
    },
    [dispatch, logbooks, navigate],
  );

  return (
    <Flex
      gap={20}
      justify="space-between"
      align="center"
      wrap="wrap"
      {...props}>
      {showVerifyCount && (
        <Flex direction="row" gap={4}>
          <Badge variant="light" size="lg" color={"orange.4"} py={10} miw={80}>
            <Flex gap={6} align="center">
              Requires Verification
              <Text color={"orange.8"}>
                {
                  reviewData?.filter(
                    e =>
                      [1, 2].includes(e.status) &&
                      !e.primaryValid &&
                      (e.lbentry.sign === "both" || e.status < 3),
                  ).length
                }
              </Text>
            </Flex>
          </Badge>
          <Badge variant="light" size="lg" color={"orange.4"} py={10} miw={80}>
            <Flex gap={6} align="center">
              Requires Co-Sign
              <Text color={"orange.8"}>
                {
                  reviewData?.filter(
                    e =>
                      [1, 2].includes(e.status) &&
                      !e.secondaryValid &&
                      (e.lbentry.sign === "both" ||
                        (e.lbentry.sign && e.lbentry.sign === "both")),
                  ).length
                }
              </Text>
            </Flex>
          </Badge>
        </Flex>
      )}
      <Flex
        style={{ flex: 1, justifyContent: "center" }}
        gap={20}
        align="center"
        wrap="wrap">
        <Flex gap={15} align="center">
          <Text>Entries</Text>
          <Select
            name="entry"
            ref={entriesRef}
            miw={idType === "meid" ? "400px" : "315px"}
            placeholder={
              entries?.length !== 0 ? "Choose an entry" : "No Entries"
            }
            data={entriesSelect.map(e => ({
              ...e,
              label: `${e.label.split(" - ")[0]}${
                e.patient && e.patient.trim() ? " - " + e.patient.trim() : ""
              }`,
            }))}
            itemComponent={EntrySelectItem}
            value={isNewEntry ? null : (lbeidOrPartId ?? latestEntry.id)}
            onChange={handleSelectChange}
          />
        </Flex>
        {!isExpiredLogbook && (
          <Flex gap={15} align="center">
            <Text>Logbooks</Text>
            <Select
              data-cy="logbook-select"
              searchable
              miw={"315px"}
              placeholder="Pick one"
              value={collectionId || logbookId}
              onChange={handleLogbookChange}
              data={logbooks}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default memo(LogBarSelects);
